import { cn } from "@/lib/utils";

/* Bones sit exactly where the loaded content will, at the same sizes, so the
   page does not reflow when data lands. Card chrome stays still; only the
   bones breathe. */
function Bone({ className }: { className?: string }) {
  return <div className={cn("rounded bg-muted", className)} />;
}

function CardBone({
  className,
  children,
  wide = false,
}: {
  className?: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card", className)}>
      <div className="flex items-center justify-between px-5 pt-4 pb-3 animate-pulse">
        <Bone className="h-4 w-28" />
        {wide && <Bone className="h-3 w-12" />}
      </div>
      {children}
    </div>
  );
}

function TableBone({ rows, columns }: { rows: number; columns: number[] }) {
  const total = columns.reduce((a, b) => a + b, 0);
  const widths = columns.map((c) => `${(c / total) * 100}%`);
  return (
    <div className="px-3 pb-3 animate-pulse">
      <div className="flex items-center gap-6 border-b border-border px-2 py-1.5">
        {widths.map((w, i) => (
          <div key={i} style={{ width: w }}>
            <Bone className="h-2.5 w-1/2 max-w-16" />
          </div>
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex h-10 items-center gap-6 border-b border-border px-2 last:border-b-0">
          {widths.map((w, i) => (
            <div key={i} style={{ width: w }}>
              <Bone className={cn("h-3.5", i === 0 ? "w-3/4" : "w-1/2")} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function CellBone() {
  return (
    <div className="flex flex-col gap-1 border-l border-t border-border px-5 py-4">
      <Bone className="my-0.5 h-3.5 w-20" />
      <Bone className="my-1 h-7 w-16" />
      <Bone className="my-0.5 h-3 w-28" />
    </div>
  );
}

export function AffiliateDashboardSkeleton({ withSelect = false }: { withSelect?: boolean }) {
  return (
    <div className="flex-1 space-y-4" aria-busy="true" aria-label="Loading dashboard">
      <div className="mb-6 flex items-center justify-between gap-4 animate-pulse sm:mb-8">
        <Bone className="h-7 w-32" />
        {withSelect && <Bone className="h-10 w-56 rounded-md" />}
      </div>

      <CardBone>
        <div className="-ml-px grid grid-cols-1 lg:grid-cols-12">
          <div className="flex flex-col border-l border-t border-border px-5 py-5 animate-pulse lg:col-span-5 lg:py-6">
            <Bone className="my-0.5 h-3.5 w-16" />
            <Bone className="mt-2 h-11 w-36" />
            <Bone className="mt-3 h-3 w-32" />
          </div>
          <div className="grid grid-cols-1 animate-pulse sm:grid-cols-2 lg:col-span-7">
            {Array.from({ length: 4 }).map((_, i) => (
              <CellBone key={i} />
            ))}
          </div>
        </div>
      </CardBone>

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="relative min-w-0 lg:col-span-8">
          <div className="lg:absolute lg:inset-0">
            <CardBone className="lg:h-full">
              <TableBone rows={6} columns={[2, 2, 2, 2]} />
            </CardBone>
          </div>
        </div>
        <div className="min-w-0 space-y-4 lg:col-span-4">
          <CardBone wide>
            <div className="px-5 pb-5 animate-pulse">
              <Bone className="h-10 w-24" />
              <Bone className="mt-3 h-3.5 w-full" />
              <Bone className="mt-2 h-3.5 w-2/3" />
            </div>
          </CardBone>
          <CardBone wide>
            <div className="px-5 pb-5 animate-pulse">
              <Bone className="h-8 w-36" />
              <Bone className="mt-3 h-1.5 w-full rounded-full" />
              <Bone className="mt-3 h-3 w-3/4" />
            </div>
          </CardBone>
          <CardBone wide>
            <TableBone rows={3} columns={[2, 2, 2]} />
          </CardBone>
        </div>
      </div>
    </div>
  );
}
